import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export const listenerApi = createApi({
  reducerPath: "listenerApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    listenerList: builder.query({
      query: ({ page = 1, pageSize = 10, searchParams, fromDate,toDate, archived }) => ({
        url: `listener/listener-list`,
        method: "GET",
        params: { page, pageSize, searchParams, fromDate,toDate, archived },
      }),
    }),
    applications: builder.query({
      query: ({ page = 1, pageSize = 10, searchParams, date }) => ({
        url: `listener/listener-applications`,
        method: "GET",
        params: { page, pageSize, searchParams, date },
      }),
    }),
    profileApprovals: builder.query({
      query: ({ page = 1, pageSize = 10, searchParams, date }) => ({
        url: `listener/listener-profiles`,
        method: "GET",
        params: { page, pageSize, searchParams, date },
      }),
    }),
    listenerFormLink: builder.mutation({
      query: (userId) => ({
        url: `listener/listener-form-link`,
        method: "POST",
        body: { id: userId },
      }),
    }),
    listenerProfileFormLink: builder.mutation({
      query: (userId) => ({
        url: `listener/listener-profile-form-link`,
        method: "POST",
        body: { id: userId },
      }),
    }),

    rejectRequest: builder.mutation({
      query: ({ userId, reason, text }) => ({
        url: `listener/reject-request`,
        method: "POST",
        body: { userId, reason, text },
      }),
    }),
    listenerProfileApproval: builder.mutation({
      query: (userId) => ({
        url: `listener/listener-request-approval`,
        method: "POST",
        body: { userId },
      }),
    }),
    listenerProfile: builder.query({
      query: (id) => ({
        url: `listener/listener-profile/${id}`,
        method: "GET",
      }),
    }),
    listenerDelete: builder.mutation({
      query: (id) => ({
        url: `user/hard-delete-user`,
        method: "DELETE",
        body: { id },
      }),
    }),
    listenerSoftDelete: builder.mutation({
      query: ({ id, status, mobile_number }) => ({
        url: `user/soft-delete-listener`,
        method: "PUT",
        body: { id, status, mobile_number },
      }),
    }),
    sessionList: builder.query({
      query: ({ page = 1, limit = 10, search, searchUser, searchListener, fromDate, toDate, type, status }) => ({
        url: `listener/sessions-list`,
        method: "GET",
        params: { page, limit, search, searchUser, searchListener, fromDate, toDate, type, status },
      }),
    }),
    giftList: builder.query({
      query: ({ page = 1, limit = 10, search }) => ({
        url: `listener/gifts-list`,
        method: "GET",
        params: { page, limit, search },
      }),
    }),
    rechargeList: builder.query({
      query: ({ page = 1, limit = 10, search, fromDate, toDate }) => ({
        url: `listener/recharges-list`,
        method: "GET",
        params: { page, limit, search, fromDate, toDate },
      }),
    }),
    payoutsList: builder.query({
      query: ({ page = 1, limit = 10, search, fromDate, toDate }) => ({
        url: `listener/payouts-list`,
        method: "GET",
        params: { page, limit, search, fromDate, toDate },
      }),
    }),
    revenue: builder.query({
      query: () => ({
        url: `listener/revenue`,
        method: "GET",
      }),
    }),
    editSalary: builder.mutation({
      query: ({ id, updates }) => ({
        url: `/listener/update-payout`,
        method: "PUT",
        body: { id, updates },
      }),
    }),
    getSinglePayout: builder.query({
      query: (id) => ({
        url: `/listener/single-payout?id=${id}`,
        method: "GET",
      }),
    }),
    paySalaryUser: builder.mutation({
      query: (details) => ({
        url: "/listener/salary-payout",
        method: "POST",
        body: details,
      }),
    }),
    paySalary: builder.mutation({
      query: ({ id, amount ,transaction_id}) => ({
        url: `/listener/pay-salary`, 
        method: "POST",
        body: { id, amount,transaction_id },
      }),
    }),
    createSession: builder.mutation({
      query: (data) => ({
        url: "/session/create-session",
        method: "POST",
        body: data,
      }),
    }),

    // ── Profile image approvals ──────────────────────────────────────────────
    getPendingImages: builder.query({
      query: ({ page = 1, pageSize = 20 } = {}) => ({
        url: "listener/pending-images",
        method: "GET",
        params: { page, pageSize },
      }),
    }),
    approveImage: builder.mutation({
      query: ({ imageId, set_as_display = false }) => ({
        url: `listener/approve-image/${imageId}`,
        method: "PUT",
        body: { set_as_display },
      }),
    }),
    rejectImage: builder.mutation({
      query: ({ imageId, reason }) => ({
        url: `listener/reject-image/${imageId}`,
        method: "PUT",
        body: { reason },
      }),
    }),
    deleteListenerImage: builder.mutation({
      query: (imageId) => ({
        url: `listener/delete-image/${imageId}`,
        method: "DELETE",
      }),
    }),
    addListenerImages: builder.mutation({
      query: ({ listenerId, files }) => {
        const formData = new FormData();
        formData.append("listenerId", listenerId);
        files.forEach((file) => formData.append("images", file));
        return {
          url: `user/listener-image-upload`,
          method: "POST",
          body: formData,
        };
      },
    }),

    // ── Profile text update approvals ────────────────────────────────────────
    getPendingProfileUpdates: builder.query({
      query: ({ page = 1, pageSize = 20 } = {}) => ({
        url: "listener/pending-profile-updates",
        method: "GET",
        params: { page, pageSize },
      }),
    }),
    approveProfileUpdate: builder.mutation({
      query: (listenerId) => ({
        url: `listener/approve-profile-update/${listenerId}`,
        method: "PUT",
      }),
    }),
    rejectProfileUpdate: builder.mutation({
      query: (listenerId) => ({
        url: `listener/reject-profile-update/${listenerId}`,
        method: "PUT",
      }),
    }),

    // ── Payment reconciliation (Razorpay vs DB) ──────────────────────────────
    paymentReconciliation: builder.query({
      query: ({ fromDate, toDate } = {}) => ({
        url: `reconcile/payments`,
        method: "GET",
        params: { fromDate, toDate },
      }),
    }),

    // ── Wallet ledger reconciliation ─────────────────────────────────────────
    walletReconciliation: builder.query({
      query: ({ ownerType, ownerId }) => ({
        url: `reconcile/wallet`,
        method: "GET",
        params: { ownerType, ownerId },
      }),
    }),
    walletReconciliationSweep: builder.query({
      query: () => ({
        url: `reconcile/wallet/sweep`,
        method: "GET",
      }),
    }),
    walletActivity: builder.query({
      query: ({ page = 1, pageSize = 20, search } = {}) => ({
        url: `reconcile/wallet/activity`,
        method: "GET",
        params: { page, pageSize, search },
      }),
    }),
    resolvePaymentDiscrepancy: builder.mutation({
      query: ({ dbType, dbId, razorpayPaymentId, note }) => ({
        url: `reconcile/payments/resolve`,
        method: "POST",
        body: { dbType, dbId, razorpayPaymentId, note },
      }),
    }),
    userSpendSummary: builder.query({
      query: ({ page = 1, pageSize = 20, search } = {}) => ({
        url: `reconcile/users/spend-summary`,
        method: "GET",
        params: { page, pageSize, search },
      }),
    }),
  }),
});

export const {
  useListenerListQuery,
  useListenerFormLinkMutation,
  useProfileApprovalsQuery,
  useApplicationsQuery,
  useListenerProfileFormLinkMutation,
  useRejectRequestMutation,
  useListenerProfileApprovalMutation,
  useListenerProfileQuery,
  useListenerDeleteMutation,
  useListenerSoftDeleteMutation,
  useSessionListQuery,
  useGiftListQuery,
  useRechargeListQuery,
  useRevenueQuery,
  usePayoutsListQuery,
  useEditSalaryMutation,
  useGetSinglePayoutQuery,
  usePaySalaryUserMutation,
  usePaySalaryMutation,
  useCreateSessionMutation,
  useGetPendingImagesQuery,
  useApproveImageMutation,
  useRejectImageMutation,
  useDeleteListenerImageMutation,
  useAddListenerImagesMutation,
  useGetPendingProfileUpdatesQuery,
  useApproveProfileUpdateMutation,
  useRejectProfileUpdateMutation,
  usePaymentReconciliationQuery,
  useWalletReconciliationQuery,
  useWalletReconciliationSweepQuery,
  useWalletActivityQuery,
  useResolvePaymentDiscrepancyMutation,
  useUserSpendSummaryQuery,
} = listenerApi;
